// 10/17 Josh: the #explore cards drift slowly right to left in one endless row, and people can move it by hand
// (swipe, trackpad, mouse drag; an optional .fusion-scrollbar under it, removed 10/17 by Josh). The row is a real scroll box: the drift only nudges
// scrollLeft, so any manual scroll just takes over. It pauses while someone is using it and resumes RESUME_MS later.
// Endless: the cards are repeated (copies aria-hidden + inert, so screen readers and the keyboard meet each card
// once) and the position is kept inside the second set, so scrolling never hits an end in either direction.
// Reduced motion: no drift and no copies; a plain scroll row with the same bar.
(() => {
  'use strict';
  const wrap = document.querySelector('#explore .fusion-marquee');
  const track = wrap?.querySelector('.fusion-track');
  const bar = document.querySelector('#explore .fusion-scrollbar');
  if (!track) return;
  const [thumb, ghost] = bar ? bar.querySelectorAll('.fusion-thumb') : [];
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const SPEED = 26;        // px per second
  const RESUME_MS = 2500;  // idle time after a manual scroll before the drift picks up again
  const originals = [...track.children];
  let copies = [], loop = false, set = 0, pos = 0, written = -1;
  let hovering = false, holding = false, idleUntil = 0, onScreen = true, last = 0;

  function build() {
    const progress = set ? ((pos - (loop ? set : 0)) / set) : 0;
    copies.forEach(c => c.remove());
    copies = [];
    loop = !reduced.matches;
    wrap.classList.toggle('is-looping', loop);
    set = track.getBoundingClientRect().width;
    if (loop && set) {
      // the position lives in set 2; a set before it and at least a set + a view after it leave room for a long swipe
      const sets = 3 + Math.ceil(wrap.clientWidth / set);
      for (let s = 1; s < sets; s++) {
        originals.forEach(card => {
          const copy = card.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.inert = true;
          copy.querySelectorAll('a').forEach(a => { a.tabIndex = -1; });
          copies.push(copy);
        });
      }
      track.append(...copies);
      setPos(set + progress * set);
    }
    if (bar) bar.hidden = false;
    paint();
  }

  // keep the position inside the second set (the copies on both sides make the jump invisible)
  function wrapPos(p) { return loop ? set + (((p - set) % set) + set) % set : p; }
  function setPos(p) {
    pos = wrapPos(p);
    wrap.scrollLeft = pos;
    written = wrap.scrollLeft;
  }

  function paint() {
    if (!bar) return;
    const tw = bar.clientWidth;
    let frac, size;
    if (loop) {
      frac = (pos - set) / set;
      size = Math.min(0.4, Math.max(0.14, wrap.clientWidth / set));   // capped so the drift reads on the bar
    } else {
      const max = wrap.scrollWidth - wrap.clientWidth;
      frac = max > 0 ? wrap.scrollLeft / max : 0;
      size = Math.min(1, wrap.clientWidth / wrap.scrollWidth);
    }
    const w = Math.max(28, size * tw), x = loop ? frac * tw : frac * (tw - w);
    thumb.style.width = ghost.style.width = w + 'px';
    thumb.style.transform = `translateX(${x}px)`;
    // endless row: the part of the thumb that runs off the right end comes back in on the left
    ghost.style.transform = `translateX(${x - tw}px)`;
    ghost.hidden = !loop || x + w <= tw;
    bar.setAttribute('aria-valuenow', Math.round(frac * 100));
  }

  const paused = () => !loop || hovering || holding || wrap.matches(':focus-within') || !!bar?.matches(':focus') || performance.now() < idleUntil;
  const touched = () => { idleUntil = performance.now() + RESUME_MS; };

  function tick(now) {
    const dt = last ? Math.min(now - last, 100) / 1000 : 0;
    last = now;
    if (onScreen && !document.hidden && !paused()) setPos(pos + SPEED * dt);
    requestAnimationFrame(tick);
  }

  // a scroll we didn't write = someone moved it (touch, trackpad, keyboard focus, find-in-page)
  wrap.addEventListener('scroll', () => {
    if (Math.abs(wrap.scrollLeft - written) > 1) {
      touched();
      pos = wrap.scrollLeft;
      written = pos;
      clearTimeout(wrap._settle);
      // re-centre in the middle set once the gesture settles (doing it mid-swipe would cut the momentum)
      wrap._settle = setTimeout(() => { if (loop) setPos(pos); }, 140);
    }
    paint();
  }, { passive: true });
  wrap.addEventListener('wheel', touched, { passive: true });
  wrap.addEventListener('touchstart', () => { holding = true; touched(); }, { passive: true });
  wrap.addEventListener('touchend', () => { holding = false; touched(); }, { passive: true });
  wrap.addEventListener('mouseenter', () => { if (finePointer.matches) hovering = true; });
  wrap.addEventListener('mouseleave', () => { hovering = false; });
  wrap.addEventListener('focusin', touched);

  // mouse: click-and-drag the row; a drag never counts as a click on a card
  let drag = null;
  wrap.addEventListener('pointerdown', e => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    drag = { x: e.clientX, start: pos, moved: false, id: e.pointerId };
  });
  wrap.addEventListener('pointermove', e => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) > 5) {
      drag.moved = true;
      holding = true;
      wrap.setPointerCapture(e.pointerId);
      wrap.classList.add('is-dragging');
    }
    if (drag.moved) { setPos(drag.start - dx); touched(); paint(); }
  });
  const endDrag = e => {
    if (!drag || e.pointerId !== drag.id) return;
    if (drag.moved) {
      wrap.addEventListener('click', ev => { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true });
      touched();
    }
    holding = false;
    wrap.classList.remove('is-dragging');
    drag = null;
  };
  wrap.addEventListener('pointerup', endDrag);
  wrap.addEventListener('pointercancel', endDrag);
  wrap.addEventListener('dragstart', e => e.preventDefault());

  if (bar) {
    // the bar: drag the thumb, click the track to jump there, arrow keys step one card
    function toPos(frac) {
      if (loop) return set + frac * set;
      return frac * (wrap.scrollWidth - wrap.clientWidth);
    }
    let barDrag = null;
    bar.addEventListener('pointerdown', e => {
      if (e.button !== 0) return;
      e.preventDefault();
      const r = bar.getBoundingClientRect();
      const onThumb = e.target.classList.contains('fusion-thumb');
      if (!onThumb) {
        // jump so the thumb centres on the click
        const tw = r.width, w = thumb.getBoundingClientRect().width;
        const frac = loop ? (e.clientX - r.left - w / 2) / tw : (e.clientX - r.left - w / 2) / Math.max(1, tw - w);
        setPos(toPos(loop ? frac : Math.min(1, Math.max(0, frac))));
      }
      barDrag = { x: e.clientX, start: pos, id: e.pointerId, tw: r.width, w: thumb.getBoundingClientRect().width };
      holding = true;
      bar.setPointerCapture(e.pointerId);
      bar.classList.add('is-dragging');
      touched(); paint();
    });
    bar.addEventListener('pointermove', e => {
      if (!barDrag || e.pointerId !== barDrag.id) return;
      const dx = e.clientX - barDrag.x;
      const per = loop ? set / barDrag.tw : (wrap.scrollWidth - wrap.clientWidth) / Math.max(1, barDrag.tw - barDrag.w);
      const p = barDrag.start + dx * per;
      setPos(loop ? p : Math.min(wrap.scrollWidth - wrap.clientWidth, Math.max(0, p)));
      touched(); paint();
    });
    const endBar = e => {
      if (!barDrag || e.pointerId !== barDrag.id) return;
      barDrag = null; holding = false;
      bar.classList.remove('is-dragging');
      touched();
    };
    bar.addEventListener('pointerup', endBar);
    bar.addEventListener('pointercancel', endBar);
    bar.addEventListener('keydown', e => {
      const step = (originals[0]?.getBoundingClientRect().width || 300) + 18;
      const d = { ArrowRight: step, ArrowLeft: -step, PageDown: wrap.clientWidth, PageUp: -wrap.clientWidth }[e.key];
      if (d === undefined) return;
      e.preventDefault();
      touched();
      wrap.scrollBy({ left: d, behavior: reduced.matches ? 'auto' : 'smooth' });
    });
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([en]) => { onScreen = en.isIntersecting; }).observe(wrap);
  }
  build();
  requestAnimationFrame(tick);
  reduced.addEventListener('change', build);
  let timer, lastWidth = innerWidth;
  addEventListener('resize', () => {
    if (innerWidth === lastWidth) { paint(); return; }   // phone toolbars change the height only
    lastWidth = innerWidth;
    clearTimeout(timer);
    timer = setTimeout(build, 200);
  });
})();
